import { DateTime, Interval } from "luxon";
import { DEFAULT_RESERVATION_DURATION } from "constants/reservation";

// Admin-side helpers for staff-created appointments (plan §7, phase 6).
//
// The customer flow books through the slot engine, which validates hours, buffers,
// lead time, and overlap server-side. Staff booking deliberately bypasses all of that
// (§7: "skip lead-time enforcement for staff"), writing straight to `reservations` the
// same way the restaurant modal does — so the appointment-shaped fields have to be
// assembled here instead. The shape mirrors `buildAppointmentFields()` in
// functions/src/waitlistAPI/helpers/appointmentBooking.helper.ts and must be kept in
// sync with it by hand, the same arrangement appointmentService.helper.js uses.

/**
 * How long a staff-created appointment runs.
 *
 * The service's duration is the default, but staff can override it — §7 keeps that
 * escape hatch for the case where an existing appointment needs cloning at a slightly
 * different scope. An explicit override always wins, including one that happens to
 * equal the service duration.
 */
export const resolveAppointmentDuration = (formDuration, service) => {
  const override = Number(formDuration);
  if (Number.isFinite(override) && override > 0) {
    return override;
  }
  const fromService = Number(service?.duration);
  if (Number.isFinite(fromService) && fromService > 0) {
    return fromService;
  }
  return DEFAULT_RESERVATION_DURATION;
};

/**
 * Duration options for the staff picker: the standard 15-minute ladder, plus the
 * chosen service's own duration when it is not a multiple of 15. A 20-minute service
 * would otherwise have no option matching itself, and staff would have to round.
 */
export const durationOptionsForService = (baseOptions, service) => {
  const duration = Number(service?.duration);
  if (!Number.isFinite(duration) || duration <= 0 || baseOptions.some(option => Number(option.value) === duration)) {
    return baseOptions;
  }
  const label =
    `${Math.floor(duration / 60) ? `${Math.floor(duration / 60)}h ` : ""}${duration % 60 ? `${duration % 60}m` : ""}`.trim();
  return [...baseOptions, { value: duration, label }].sort((a, b) => Number(a.value) - Number(b.value));
};

// cancelled, confirmedCancel, deleted, walkout — mirrors VisitStatus in
// services/visits/visits.service.js, which is not imported here so this module stays
// free of Firebase-touching imports and remains unit-testable (plan §15.6).
const RELEASED_STATUSES = [7, 8, 9, 10];

/**
 * The first existing appointment on this resource that overlaps [start, start + duration).
 *
 * Overlap is half-open, matching the slot engine (plan §12.5) — an appointment ending
 * at 7:30 does not collide with one starting at 7:30. Returns null when nothing clashes.
 *
 * This *warns*, it does not block: §7 gives staff an override, and refusing the write
 * would make the admin view less capable than the phone call it is replacing. Buffers
 * are ignored on purpose — they shape what customers may self-book, not what staff may
 * deliberately squeeze in.
 */
export const findAppointmentConflict = ({ reservations = [], resourceId, start, duration, timezone, ignoreId }) => {
  if (!resourceId || !start) {
    return null;
  }
  const startsAt = DateTime.fromJSDate(start instanceof Date ? start : new Date(start)).setZone(timezone);
  if (!startsAt.isValid) {
    return null;
  }
  const minutes = Number(duration) > 0 ? Number(duration) : DEFAULT_RESERVATION_DURATION;
  const wanted = Interval.fromDateTimes(startsAt, startsAt.plus({ minutes }));

  const conflict = reservations.find(doc => {
    const data = typeof doc?.data === "function" ? doc.data() : doc;
    if (!data || doc.id === ignoreId || data.resourceId !== resourceId) {
      return false;
    }
    // A cancelled or deleted appointment does not hold its slot.
    if (RELEASED_STATUSES.includes(data.status)) {
      return false;
    }
    const existingStart = DateTime.fromJSDate(data.reservationTime?.toDate?.() || data.reservationTime).setZone(
      timezone
    );
    if (!existingStart.isValid) {
      return false;
    }
    const existingMinutes = Number(data.duration) > 0 ? Number(data.duration) : DEFAULT_RESERVATION_DURATION;
    return wanted.overlaps(Interval.fromDateTimes(existingStart, existingStart.plus({ minutes: existingMinutes })));
  });

  return conflict || null;
};

/**
 * Resources that perform this service, in the order they were given.
 *
 * Unlike the customer flow this never *removes* the others — §7 lets staff book any
 * resource. The caller uses the split to group the dropdown, so the ones that actually
 * perform the service come first and the rest stay reachable below.
 */
export const splitResourcesByService = (resources = [], serviceId) => {
  if (!serviceId) {
    return { performing: resources, others: [] };
  }
  return {
    performing: resources.filter(resource => (resource.serviceIds || []).includes(serviceId)),
    others: resources.filter(resource => !(resource.serviceIds || []).includes(serviceId))
  };
};
